import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import NotFound from './pages/NotFound';
import Index from './pages/Index';
import Layout from './Layout';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidUpdate(prevProps) {
    // If the user navigates to a different route, reset the boundary so the new page can render.
    // Without this, errors in Layout (which persists across routes) can "stick" across navigation.
    if (
      this.state.hasError &&
      prevProps.resetKey !== this.props.resetKey
    ) {
      this.setState({ hasError: false, error: null });
    }
  }

  componentDidCatch(error, errorInfo) {
    const componentName = this.props.componentName || 'Unknown Component';
    console.error(`Component error in ${componentName}:`, error, errorInfo);
    
    // Extract more detailed error information
    const errorMessage = error.message || 'Unknown error';
    const componentStack = errorInfo.componentStack || '';
    
    // Try to extract function/component name from error stack
    let functionName = componentName;
    if (error.stack) {
      // Look for function names in the stack trace
      const functionMatch = error.stack.match(/at\s+([A-Za-z][A-Za-z0-9_]*)\s*\(/);
      if (functionMatch && functionMatch[1] && functionMatch[1] !== 'ErrorBoundary') {
        functionName = functionMatch[1];
      }
    }
    
    console.error(`Detailed error in ${componentName} (${functionName}):`, {
      error: errorMessage,
      component: componentName,
      function: functionName,
      stack: error.stack,
      componentStack: componentStack
    });
  }

  render() {
    if (this.state.hasError) {
      const componentName = this.props.componentName || 'Unknown Component';
      return (
        <div style={{ padding: '1rem', background: '#fee', border: '1px solid #fcc', borderRadius: '4px', margin: '1rem' }}>
          <h3>Error in {componentName}</h3>
          <p>{this.state.error?.message || 'Unknown error'}</p>
          <details style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
            <summary style={{ cursor: 'pointer', color: '#666' }}>Error Details</summary>
            <pre style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#666', whiteSpace: 'pre-wrap' }}>
              {this.state.error?.stack || 'No stack trace available'}
            </pre>
          </details>
          <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              style={{
                padding: '0.4rem 0.75rem',
                background: '#fff',
                border: '1px solid #fcc',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Try again
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '0.4rem 0.75rem',
                background: '#c00',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Reload
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const futureConfig ={
        v7_startTransition: true,
        v7_relativeSplatPath: true
      };
export default function RootApp() {
  // Determine basename based on routing mode
  const basename = window.location.pathname.startsWith('/apps/') 
    ? window.location.pathname.split('/').slice(0, 3).join('/') 
    : '/';

      const RouterContent = () => {
        const location = useLocation();
        const path = location.pathname;
        
        // Function to get current page name based on route
        const getCurrentPageName = () => {
          if (path === "/") return "Index";
          return "Index"; // default fallback
        };
        
        const currentPageName = getCurrentPageName();
        
        return (
          <>
            <ErrorBoundary componentName="Layout" resetKey={path}>
            <Layout currentPageName={currentPageName}>
      <Routes>
        <Route path="/login" element={<Login currentPageName="Login" />} />
        <Route path="/auth/reset-password" element={<ResetPassword currentPageName="ResetPassword" />} />
        <Route path="/" element={<ErrorBoundary componentName="Index" resetKey={path}><Index currentPageName="Index" /></ErrorBoundary>} />
        <Route path="/index" element={<ErrorBoundary componentName="Index" resetKey={path}><Index currentPageName="Index" /></ErrorBoundary>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
            </Layout>
          </ErrorBoundary>
          </>
        );
      };

  return (
    <BrowserRouter 
      basename={basename}
      future={futureConfig}
    >
      <RouterContent />
    </BrowserRouter>
  );
}
